import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, List, ListItem, ListItemIcon, ListItemText, Typography,
} from '@mui/material'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CategoryIcon from '@mui/icons-material/Category'
import PlaceIcon from '@mui/icons-material/Place'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { FC, useEffect, useRef, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { useNavigate, useParams } from 'react-router-dom'
import { getJson, getUrl } from '../providers/data'
import { VerzoekDetail as VerzoekDetailData } from '../types/apiRequests'
import {
  DoelVerzoekKey, VerzoekTypeKey,
  doelVerzoekColor, formatDate, verzoekTypeColor,
} from './verzoekenoverzichtConstants'

// Fix Leaflet default marker icon (broken in webpack)
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, no-underscore-dangle
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

interface Geometrie {
  type: string
  coordinates?: number[] | number[][][]
  geometries?: Geometrie[]
}

interface DetailFieldProps {
  label: string
  children: React.ReactNode
}

const DetailField: FC<DetailFieldProps> = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Box>{children}</Box>
  </Box>
)

interface LocatieData {
  geometrie?: Geometrie
}

interface LocationMapProps {
  locatie: LocatieData
}

const LocationMap: FC<LocationMapProps> = ({ locatie }) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<L.Map | null>(null)

  useEffect(() => {
    if (!mapRef.current || !locatie?.geometrie || mapInstance.current) return

    const geom = locatie.geometrie
    if (!geom) return
    const map = L.map(mapRef.current, { scrollWheelZoom: false })
    mapInstance.current = map

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map)

    if (geom.type === 'Point' && geom.coordinates) {
      const [lng, lat] = geom.coordinates as number[]
      L.marker([lat, lng]).addTo(map)
      map.setView([lat, lng], 15)
    } else if (geom.type === 'Polygon' && geom.coordinates) {
      const coords = (geom.coordinates as number[][][]).map(ring =>
        ring.map(([lng, lat]) => [lat, lng] as [number, number]),
      )
      const polygon = L.polygon(coords, { color: '#31679B', weight: 2 }).addTo(map)
      map.fitBounds(polygon.getBounds(), { padding: [30, 30] })
    } else if (geom.type === 'GeometryCollection' && geom.geometries) {
      const group = L.featureGroup()
      for (const g of geom.geometries) {
        if (g.type === 'Polygon' && g.coordinates) {
          const coords = (g.coordinates as number[][][]).map(ring =>
            ring.map(([lng, lat]) => [lat, lng] as [number, number]),
          )
          L.polygon(coords, { color: '#31679B', weight: 2 }).addTo(group)
        }
      }
      group.addTo(map)
      if (group.getLayers().length > 0) {
        map.fitBounds(group.getBounds(), { padding: [30, 30] })
      }
    }

    return () => {
      map.remove()
      mapInstance.current = null
    }
  }, [locatie])

  return <div ref={mapRef} style={{ height: 350, width: '100%', borderRadius: 4 }} />
}

interface OrgCardProps {
  label: string
  org: { naam: string } | null | undefined
}

const OrgCard: FC<OrgCardProps> = ({ label, org }) => (
  <DetailField label={label}>
    {org ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceIcon fontSize="small" color="action" />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{org.naam}</Typography>
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary">–</Typography>
    )}
  </DetailField>
)

const VerzoekDetail: FC = () => {
  const { verzoeknummer } = useParams<{ verzoeknummer: string }>()
  const translate = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()

  const [detail, setDetail] = useState<VerzoekDetailData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const data = await getJson<VerzoekDetailData>(`${getUrl()}/verzoekenoverzicht/${verzoeknummer}`)
        setDetail(data)
      } catch {
        notify('ra.notification.http_error', { type: 'error' })
      } finally {
        setLoading(false)
      }
    }
    void fetchDetail()
  }, [verzoeknummer, notify])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!detail) {
    return (
      <Box sx={{ mt: 2, ml: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => void navigate('/verzoekenoverzicht')}>
          {translate('verzoekenoverzicht.backToList')}
        </Button>
        <Typography sx={{ mt: 2 }}>{translate('verzoekenoverzicht.detail.notFound')}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 2, ml: 2, mr: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => void navigate('/verzoekenoverzicht')}
        sx={{ mb: 1 }}
      >
        {translate('verzoekenoverzicht.backToList')}
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h5">
          {detail.verzoeknummer}
        </Typography>
        <Chip
          label={detail.verzoekType}
          color={verzoekTypeColor[detail.verzoekType as VerzoekTypeKey] ?? 'default'}
          variant="outlined"
          size="small"
        />
        <Chip
          label={detail.doelVerzoek}
          color={doelVerzoekColor[detail.doelVerzoek as DoelVerzoekKey] ?? 'default'}
          variant="outlined"
          size="small"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={detail.locatie ? 7 : 12}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {translate('verzoekenoverzicht.detail.general')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <DetailField label={translate('verzoekenoverzicht.columns.verzoeknummer')}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {detail.verzoeknummer}
                    </Typography>
                  </DetailField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <DetailField label={translate('verzoekenoverzicht.columns.volgnummer')}>
                    <Typography variant="body2">{detail.volgnummer}</Typography>
                  </DetailField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <DetailField label={translate('verzoekenoverzicht.columns.datumIngediend')}>
                    <Typography variant="body2">{formatDate(detail.datumIngediend)}</Typography>
                  </DetailField>
                </Grid>
                {detail.plichtType && (
                  <Grid item xs={6} sm={4}>
                    <DetailField label={translate('verzoekenoverzicht.detail.plichtType')}>
                      <Chip label={detail.plichtType} size="small" variant="outlined" />
                    </DetailField>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <OrgCard
                    label={translate('verzoekenoverzicht.columns.bevoegdGezag')}
                    org={detail.bevoegdGezag}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <OrgCard
                    label={translate('verzoekenoverzicht.columns.behandeldienst')}
                    org={detail.behandeldienst}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {detail.activiteit && detail.activiteit.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {translate('verzoekenoverzicht.detail.activiteiten')}
                </Typography>
                <List dense disablePadding>
                  {detail.activiteit.map((act) => (
                    <Box key={act.urn}>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CategoryIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={act.naam}
                          secondary={act.urn}
                        />
                      </ListItem>
                      {act.subactiviteit?.map((sub) => (
                        <ListItem key={sub.urn} disableGutters sx={{ pl: 2 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <SubdirectoryArrowRightIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText
                            primary={sub.naam}
                            secondary={sub.urn}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </Box>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>

        {detail.locatie && (
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PlaceIcon fontSize="small" color="primary" />
                  <Typography variant="h6">
                    {translate('verzoekenoverzicht.detail.locatie')}
                  </Typography>
                </Box>
                <LocationMap locatie={detail.locatie as LocatieData} />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default VerzoekDetail
